import React, { useState } from 'react'
import { Badge, BlockStack, Button, Card, Divider, Icon, InlineStack, Layout, Page, Text } from '@shopify/polaris';
import { PlayCircleIcon } from '@shopify/polaris-icons';
import FreeShippingSetting from '../FreeShippingSetting';
import FreeShippingProducts from '../FreeShippingProducts';
import { useSelector } from 'react-redux';
import './style.css'
import constants from '../../store/constants';

const toDate = value => (value ? new Date(value) : null)

const SummaryRow = ({ label, children }) => (
    <InlineStack align="space-between" blockAlign="center" gap="300" wrap={false}>
        <Text as="span" tone="subdued">{label}</Text>
        {children}
    </InlineStack>
)

const FreeShippingRules = () => {
    const { features, currency } = useSelector(state => state.ConfigReducer)
    const { free_shipping_rule, free_products } = useSelector(state => state.ShippingReducer)
    const [free_products_feature] = useState(features.indexOf(constants.FREE_PRODUCTS) >= 0)
    const [free_schedule] = useState(features.indexOf(constants.FREE_SCHEDULED) >= 0)

    // Live values reported by the two cards, so the preview and summary follow what's typed.
    const [rule, setRule] = useState(null)
    const [products, setProducts] = useState(null)

    const r = rule ?? { ...free_shipping_rule, start: toDate(free_shipping_rule?.start), end: toDate(free_shipping_rule?.end) }
    const p = products ?? free_products ?? {}

    const ruleOn = Boolean(r.active)
    const minSpent = r.minSpent !== undefined && r.minSpent !== null && r.minSpent !== '' ? r.minSpent : null
    const scheduled = r.start && r.end
    const productCount = Array.isArray(p.items_json) ? p.items_json.length : 0
    const productMode = p.ifPaidProducts ? 'Always charge shipping' : (p.showAsOption ? 'Ship free (own option)' : 'Ship free')

    let ruleNote = 'Free shipping is off, so it is hidden at checkout.'
    if (ruleOn) ruleNote = minSpent !== null ? `Shown on orders of ${minSpent} ${currency} or more.` : 'Set a minimum spent to choose when this option appears.'

    return (
        <div className="fs-page"><Page
            fullWidth
            title="Free Shipping Rules"
            subtitle="Turn each rule on, then set it up."
        >
            <div className="fs-wrap">
                <Layout>
                    <Layout.Section>
                        <BlockStack gap="400">
                            <FreeShippingSetting disabled={!free_schedule} onStateChange={setRule} />
                            <FreeShippingProducts disabled={!free_products_feature} onStateChange={setProducts} />
                        </BlockStack>
                    </Layout.Section>

                    <Layout.Section variant="oneThird">
                        <div className="fs-sticky">
                            <BlockStack gap="400">
                                {/* Checkout preview */}
                                <BlockStack gap="200">
                                    <InlineStack align="space-between" blockAlign="center">
                                        <Text as="h2" variant="headingSm">Checkout preview</Text>
                                        <Text as="span" tone="subdued">What customers see</Text>
                                    </InlineStack>
                                    <Card background="bg-surface-secondary">
                                        <BlockStack gap="300">
                                            <Text as="h3" variant="headingSm">Shipping method</Text>
                                            <div className="fs-methods">
                                                {ruleOn ? (
                                                    <div className="fs-method free">
                                                        <span className="fs-radio" aria-hidden="true" />
                                                        <span style={{ flexGrow: 1 }}>
                                                            <BlockStack gap="050">
                                                                <Text as="span" fontWeight="semibold">{r.name || 'Free Shipping'}</Text>
                                                                <Text as="span" tone="subdued">{r.description || 'Description appears here'}</Text>
                                                            </BlockStack>
                                                        </span>
                                                        <Text as="span" fontWeight="bold">Free</Text>
                                                    </div>
                                                ) : null}
                                                <div className="fs-method">
                                                    <span className="fs-radio" aria-hidden="true" />
                                                    <span style={{ flexGrow: 1 }}><Text as="span">Your other shipping rates</Text></span>
                                                    <Text as="span" tone="subdued">[Price]</Text>
                                                </div>
                                            </div>
                                            <Text as="p" tone="subdued">{ruleNote}</Text>
                                        </BlockStack>
                                    </Card>
                                </BlockStack>

                                {/* Summary */}
                                <Card>
                                    <BlockStack gap="300">
                                        <Text as="h2" variant="headingSm">Summary</Text>
                                        <SummaryRow label="Spending rule"><Badge tone={ruleOn ? 'success' : undefined}>{ruleOn ? 'Active' : 'Inactive'}</Badge></SummaryRow>
                                        <Divider />
                                        <SummaryRow label="Minimum spent"><Text as="span" fontWeight="semibold">{minSpent !== null ? `${minSpent} ${currency}` : 'Not set'}</Text></SummaryRow>
                                        <Divider />
                                        <SummaryRow label="Schedule">
                                            <Text as="span" fontWeight="semibold" alignment="end">{scheduled ? `${new Date(r.start).toDateString()} → ${new Date(r.end).toDateString()}` : 'Not scheduled'}</Text>
                                        </SummaryRow>
                                        <Divider />
                                        <SummaryRow label="Selected products"><Text as="span" fontWeight="semibold">{productCount}</Text></SummaryRow>
                                        <Divider />
                                        <SummaryRow label="Product eligibility"><Text as="span" fontWeight="semibold" alignment="end">{productCount ? productMode : '—'}</Text></SummaryRow>
                                    </BlockStack>
                                </Card>

                                {/* Video */}
                                <Card>
                                    <InlineStack gap="300" blockAlign="center" wrap={false}>
                                        <Icon source={PlayCircleIcon} tone="critical" />
                                        <BlockStack gap="050">
                                            <Button variant="plain" url="https://www.youtube.com/watch?v=JmR2i_qtWWw" external>How to add Shipping Rate</Button>
                                            <Text as="span" variant="bodySm" tone="subdued">Video walkthrough</Text>
                                        </BlockStack>
                                    </InlineStack>
                                </Card>
                            </BlockStack>
                        </div>
                    </Layout.Section>
                </Layout>
            </div>
        </Page></div>
    )
}

export default FreeShippingRules
